/* Blind Dog Graphics — Ready-to-Press browser (Pursuit theme). Vanilla ES2020, no dependencies.
 * Hierarchy: theme → collection → style (Shopify tag URL) + search and sort. Every view has a real page URL when one exists
 * (theme/collection/style pages: /pages/<name>-dtf-transfers, server-rendered by the same section); otherwise ?cat=&col=&style=.
 * The URL bar always shows that page URL, and history entries carry the view so Back/Forward work across pages.
 * Upgrades the server-rendered page in sections/bdg-rtp-browser.liquid; grids come from the Section Rendering
 * API (sections/bdg-rtp-grid.liquid) on real collection/search URLs, so Shopify does paging, sorting and tag filters.
 * window.BDG_RTP_ADAPTER may override { gridUrl, productUrl, suggestUrl, addToCart } (tests only).
 */
(() => {
  'use strict';
  const root = document.querySelector('[data-bdg-rtp]');
  if (!root) return;

  const $ = (s, el = root) => el.querySelector(s);
  const $$ = (s, el = root) => Array.from(el.querySelectorAll(s));
  const A = window.BDG_RTP_ADAPTER || {};
  const ROOT_URL = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const PER = 24; // page size of sections/bdg-rtp-grid.liquid
  const SORTS = ['best-selling', 'created-descending', 'price-ascending', 'title-ascending'];
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const master = root.dataset.master;
  const grid = $('[data-bdg-grid]');
  const moreBtn = $('[data-bdg-more]');
  const countEl = $('[data-bdg-count]');
  const titleEl = $('[data-bdg-title]');
  const crumbsEl = $('[data-bdg-crumbs]');
  const sortEl = $('[data-bdg-sort]');
  const emptyEl = $('[data-bdg-empty]');
  const errorEl = $('[data-bdg-error]');
  const siblingsEl = $('[data-bdg-siblings]');
  const stylesEl = $('[data-bdg-styles]');
  const progressEl = $('[data-bdg-progress]');
  const progressBar = $('[data-bdg-progress-bar]');
  const fmt = new Intl.NumberFormat('en-US');
  const money = (cents) => '$' + (cents / 100).toFixed(2);
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const handleize = (s) => String(s).toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const titleize = (s) => String(s).replace(/(^|[\s-])([a-z])/g, (m, a, b) => a + b.toUpperCase());
  const ICON = {
    chevron: '<path d="M9 5l7 7-7 7"/>',
    grid: '<rect x="3.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="3.5" y="13.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="13.5" width="7" height="7" rx="1.5"/>',
  };
  const icon = (n) => `<svg class="bdg-icon" aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${ICON[n]}</svg>`;

  /* ---------------- index of the hierarchy (from the server-rendered theme panels) ---------------- */
  const panels = Object.fromEntries($$('[data-bdg-panel]').map((p) => [p.dataset.bdgPanel, p]));
  const colIndex = {};
  $$('.bdg-panel .bdg-col__link[data-bdg-col]').forEach((a) => {
    if (colIndex[a.dataset.bdgCol]) return; // a collection listed in two themes keeps its first home
    const img = a.querySelector('img');
    colIndex[a.dataset.bdgCol] = {
      cat: a.dataset.bdgCat,
      name: $('.bdg-col__name', a).textContent.trim(),
      url: a.getAttribute('href'),
      styles: (a.dataset.styles || '').split('|').map((s) => s.trim()).filter(Boolean),
      styleUrls: Object.fromEntries((a.dataset.styleUrls || '').split('|').filter(Boolean).map((kv) => { const i = kv.indexOf('='); return [kv.slice(0, i), kv.slice(i + 1)]; })),
      img: img ? img.getAttribute('src') : '',
    };
  });

  const HUB = root.dataset.hubUrl || root.dataset.pageUrl;
  const V = root.dataset;   // the view THIS page was server-rendered for
  const h1El = $('[data-bdg-h1]');
  const H1_HUB = V.viewKind === 'hub' ? h1El.textContent : '';
  const TITLE0 = document.title;
  const TITLE_SUFFIX = (document.title.match(/ [–-] [^–-]+$/) || [''])[0];
  const state = { cat: 'all', col: '', style: '', q: '', sort: 'best-selling', page: 1, pages: 1, handle: master, loading: false };
  let ctrl = null;
  let autoLoad = false;

  const track = (event, data = {}) => {
    try { if (typeof window.gtag === 'function') window.gtag('event', event, data); else (window.dataLayer = window.dataLayer || []).push({ event, ...data }); } catch (e) { /* noop */ }
    try { window.Shopify && Shopify.analytics && Shopify.analytics.publish(event, data); } catch (e) { /* noop */ }
  };

  /* ---------------- sticky offset under Pursuit's header ----------------
     Pursuit's sticky header is .shopify-section-header (it gains .is_sticky after 10px of scroll and its height can change).
     NOT .section-header: in Pursuit that class is a homepage section title. Measured on every scroll/resize, never cached. */
  const header = document.querySelector('.shopify-section-header') || document.querySelector('[data-section-type="header-section"]');
  const bar = $('[data-bdg-bar]');
  const setTop = () => {
    const b = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    document.documentElement.style.setProperty('--bdg-sticky-top', `${Math.round(b)}px`);
    if (bar) bar.classList.toggle('is-stuck', bar.getBoundingClientRect().top <= b + 1);
  };
  let rafId = 0;
  window.addEventListener('scroll', () => { cancelAnimationFrame(rafId); rafId = requestAnimationFrame(setTop); }, { passive: true });
  window.addEventListener('resize', setTop);
  setTop();
  const stickyTop = () => parseInt(getComputedStyle(document.documentElement).getPropertyValue('--bdg-sticky-top'), 10) || 0;

  /* ---------------- URLs ---------------- */
  const gridUrl = (s, page) => {
    if (A.gridUrl) return A.gridUrl(s, page);
    if (s.q) {
      const p = new URLSearchParams({ q: s.q, type: 'product', 'options[prefix]': 'last', section_id: 'bdg-rtp-grid', page });
      return `${ROOT_URL}search?${p}`;
    }
    const p = new URLSearchParams({ section_id: 'bdg-rtp-grid', sort_by: s.sort, page });
    const tag = s.style ? `/${encodeURIComponent(handleize(s.style))}` : '';
    return `${ROOT_URL}collections/${encodeURIComponent(s.handle)}${tag}?${p}`;
  };

  const withParam = (url, k, v) => { const u = new URL(url, location.origin); u.searchParams.set(k, v); return u.pathname + u.search; };
  function urlFor(s) {
    if (s.q) return withParam(HUB, 'q', s.q);
    let url = HUB;
    const p = panels[s.cat];
    if (s.col && colIndex[s.col]) {
      const c = colIndex[s.col];
      url = (s.style && c.styleUrls[s.style]) || c.url;
      if (s.style && !c.styleUrls[s.style]) url = withParam(url, 'style', handleize(s.style));
    } else if (s.cat !== 'all' && p) {
      url = p.dataset.url || withParam(HUB, 'cat', s.cat);
      if (s.style) url = withParam(url, 'style', handleize(s.style));
    } else if (s.style) url = withParam(HUB, 'style', handleize(s.style));
    const themeDefault = (p && p.dataset.feedSort) || 'best-selling';
    if (s.sort !== themeDefault) url = withParam(url, 'sort', s.sort);
    return url;
  }
  const writeUrl = (push) => {
    const s = { bdg: true, cat: state.cat, col: state.col, style: state.style, q: state.q, sort: state.sort };
    history[push ? 'pushState' : 'replaceState'](s, '', urlFor(state));
  };
  function setHeading() {
    const p = panels[state.cat];
    let name = '';
    if (state.q) name = '';
    else if (state.col && colIndex[state.col]) name = colStyleName(colIndex[state.col].name);
    else if (state.cat !== 'all' && p && !p.dataset.feedSort) name = p.dataset.title;
    h1El.textContent = name ? `${name} DTF Transfers` : (H1_HUB || h1El.textContent);
    document.title = name ? `${name} DTF Transfers${TITLE_SUFFIX}` : TITLE0;   // same shape as the theme's titles
  }

  /* ---------------- view sync ---------------- */
  function scrollRowTo(row, el) {
    if (!row || !el) return;
    const left = el.offsetLeft - row.offsetLeft - row.clientWidth / 2 + el.clientWidth / 2;
    row.scrollTo({ left: Math.max(0, left), behavior: REDUCED ? 'auto' : 'smooth' });
  }

  function syncNav() {
    $$('[data-bdg-cat], [data-bdg-col]').forEach((a) => {
      if (a.closest('[data-bdg-siblings]') || a.closest('[data-bdg-crumbs]')) return;
      const on = a.dataset.bdgCol ? a.dataset.bdgCol === state.col : a.dataset.bdgCat === (state.q ? '' : state.cat);
      a.classList.toggle('is-active', on);
      if (on) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
    });
    const view = state.q || state.col ? '' : state.cat;
    Object.entries(panels).forEach(([k, p]) => p.classList.toggle('is-active', k === view));
    $$('.bdg-tree__item[data-bdg-nav-cat]').forEach((li) => {
      if (li.dataset.bdgNavCat === state.cat) toggleSub(li.querySelector('.bdg-tree__toggle'), true);
    });
    const pill = $(`.bdg-pill[data-bdg-cat="${CSS.escape(state.q ? 'all' : state.cat)}"]`);
    scrollRowTo($('.bdg-bar__themes'), pill && pill.parentElement);
    if (sortEl) { sortEl.value = state.sort; sortEl.disabled = !!state.q; }
    renderSiblings();
    renderStyles();
    setCrumbs();
  }

  function renderSiblings() {
    const p = panels[state.cat];
    if (!state.col || !p || state.q) { siblingsEl.hidden = true; siblingsEl.innerHTML = ''; return; }
    const items = $$('.bdg-cols > .bdg-col', p).map((li) => {
      const c = li.cloneNode(true);
      c.hidden = false;
      const a = $('a', c);
      a.classList.toggle('is-active', a.dataset.bdgCol === state.col);
      return c;
    });
    const all = document.createElement('li');
    all.className = 'bdg-col bdg-siblings__all';
    all.innerHTML = `<a class="bdg-col__link" href="?cat=${esc(state.cat)}" data-bdg-cat="${esc(state.cat)}"><span class="bdg-col__img">${icon('grid')}</span><span class="bdg-col__name">All ${esc(p.dataset.short || p.dataset.title)}</span></a>`;
    siblingsEl.replaceChildren(all, ...items);
    siblingsEl.style.setProperty('--accent', p.style.getPropertyValue('--accent'));
    siblingsEl.style.setProperty('--on', p.style.getPropertyValue('--on'));
    siblingsEl.hidden = items.length < 2;
    requestAnimationFrame(() => { const act = $('.is-active', siblingsEl); scrollRowTo(siblingsEl, act && act.closest('.bdg-col')); });
  }

  const stylesFor = (s) => {
    if (s.q) return [];
    if (s.col) return (colIndex[s.col] || { styles: [] }).styles;
    const p = panels[s.cat];
    if (s.cat !== 'all' && p) return (p.dataset.styles || '').split('|').filter(Boolean);
    return (root.dataset.styles || '').split('|').filter(Boolean);
  };
  function renderStyles() {
    const list = stylesFor(state);
    if (!list.length) { stylesEl.hidden = true; stylesEl.innerHTML = ''; return; }
    const chip = (val, label) => `<a class="bdg-chip" role="button" href="${esc(urlFor({ ...state, style: val }))}" data-bdg-style="${esc(val)}" aria-pressed="${handleize(val) === handleize(state.style)}">${esc(label)}</a>`;
    stylesEl.innerHTML = '<span class="bdg-styles__label">Style</span>' + chip('', 'All') + list.map((s) => chip(s, titleize(s))).join('');
    stylesEl.hidden = false;
  }

  function setCrumbs() {
    const parts = [];
    const p = panels[state.cat];
    if (state.q) parts.push([null, `Results for “${state.q}”`]);
    else {
      if (state.cat !== 'all' && p) parts.push([state.col || state.style ? (p.dataset.url || withParam(HUB, 'cat', state.cat)) : null, p.dataset.title, { bdgCat: state.cat }]);
      if (state.col && colIndex[state.col]) parts.push([state.style ? colIndex[state.col].url : null, colIndex[state.col].name, { bdgCol: state.col, bdgCat: state.cat }]);
      if (state.style) parts.push([null, titleize(state.style)]);
    }
    crumbsEl.hidden = !parts.length;
    crumbsEl.innerHTML = '';
    if (!parts.length) return;
    const add = (href, text, data) => {
      const el = document.createElement(href ? 'a' : 'span');
      if (href) { el.href = href; Object.assign(el.dataset, data); } else el.setAttribute('aria-current', 'page');
      el.textContent = text;
      crumbsEl.append(el);
    };
    add(HUB, 'All themes', { bdgCat: 'all' });
    parts.forEach(([href, text, data]) => { crumbsEl.insertAdjacentHTML('beforeend', icon('chevron')); add(href, text, data || {}); });
  }

  // 'Halloween' + style 'ghost' = 'Halloween Ghost'; 'Christmas' + 'christmas tree' = 'Christmas Tree' (same rule as the H1 in Liquid)
  function colStyleName(cn) {
    const sl = state.style ? titleize(state.style) : '';
    return !sl ? cn : sl.toLowerCase().startsWith(cn.toLowerCase() + ' ') ? sl : `${cn} ${sl}`;
  }
  function viewTitle(payload) {
    const p = panels[state.cat];
    if (state.q) return `“${state.q}”`;
    if (state.col) return colStyleName((colIndex[state.col] || {}).name || (payload && payload.dataset.title) || '');
    if (state.cat !== 'all' && p) {
      if (p.dataset.feedSort) return p.dataset.title;
      return p.dataset.feed ? `All ${p.dataset.title}` : `${p.dataset.title} — ${(payload && payload.dataset.title) || ''}`;
    }
    return 'All designs';
  }

  const skeletons = (n = 6) => Array.from({ length: n }, () => '<li class="bdg-card bdg-card--skeleton" aria-hidden="true"><div class="bdg-card__media"></div><p></p><p></p></li>').join('');

  /* ---------------- fetching ---------------- */
  async function fetchPayload(s, page, signal) {
    const res = await fetch(gridUrl(s, page), { signal, headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(res.status);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    return doc.querySelector('.bdg-payload');
  }

  async function load({ append = false } = {}) {
    if (ctrl) ctrl.abort();
    ctrl = new AbortController();
    const page = append ? state.page + 1 : 1;
    state.loading = true;
    errorEl.hidden = true;
    root.classList.add('is-loading');
    if (!append) { titleEl.textContent = viewTitle(null); countEl.textContent = ''; grid.innerHTML = skeletons(); emptyEl.hidden = true; progressEl.textContent = ''; }
    else if (moreBtn) moreBtn.textContent = 'Loading…';
    try {
      const payload = await fetchPayload(state, page, ctrl.signal);
      const items = payload ? Array.from(payload.querySelectorAll('[data-bdg-items] > li')) : [];
      const seen = new Set(append ? $$('.bdg-card[data-handle]', grid).map((li) => li.dataset.handle) : []);
      const fresh = items.filter((li) => !seen.has(li.dataset.handle));
      if (!append) grid.innerHTML = '';
      grid.append(...fresh);
      state.page = page;
      state.pages = payload ? +payload.dataset.pages || 1 : 1;
      const total = payload ? +payload.dataset.total || 0 : 0;
      const shown = $$('.bdg-card[data-handle]', grid).length;
      const more = state.page < state.pages;
      countEl.textContent = state.q ? (shown ? `${fmt.format(shown)}${more ? '+' : ''} designs found` : '') : `${fmt.format(total)} designs`;
      const heroN = $('.bdg-hero__sub strong'); if (heroN) heroN.textContent = state.q ? (shown ? `${fmt.format(shown)}${more ? '+' : ''} designs found` : '') : `${fmt.format(total)} designs`;   // keep the hero in step with the grid
      titleEl.textContent = viewTitle(payload);
      // A page of non-premade search hits can be empty while later pages aren't — keep going.
      if (!fresh.length && more) { state.loading = false; return load({ append: true }); }
      emptyEl.hidden = shown > 0;
      if (!shown) {
        $('[data-bdg-empty-title]').textContent = state.q ? `No designs match “${state.q}” yet.` : 'Nothing here yet.';
        if (state.q) track('bdg_rtp_search_no_results', { search_term: state.q });
      }
      if (moreBtn) {
        moreBtn.hidden = !more;
        moreBtn.textContent = 'Show more designs';
        moreBtn.href = gridUrl(state, state.page + 1).replace(/[?&]section_id=bdg-rtp-grid/, '');
      }
      const denom = state.q ? shown : Math.max(total, shown);
      progressEl.textContent = shown ? `Showing ${fmt.format(shown)} of ${fmt.format(denom)}${state.q && more ? '+' : ''}` : '';
      if (progressBar) progressBar.style.width = `${denom ? Math.min(100, (shown / denom) * 100) : 100}%`;
    } catch (e) {
      if (e.name === 'AbortError') return;
      errorEl.hidden = false;
      if (!append) { grid.innerHTML = ''; countEl.textContent = ''; progressEl.textContent = ''; if (moreBtn) moreBtn.hidden = true; }
      if (moreBtn) moreBtn.textContent = 'Show more designs';
    } finally {
      state.loading = false;
      root.classList.remove('is-loading');
    }
  }

  /* ---------------- state transitions ---------------- */
  function resolveHandle() {
    if (state.col) return state.col;
    const p = panels[state.cat];
    if (state.cat !== 'all' && p) {
      if (p.dataset.feed) return p.dataset.feed;
      const first = p.querySelector('.bdg-col__link[data-bdg-col]');
      if (first) return first.dataset.bdgCol;
    }
    return master;
  }

  function apply({ cat, col, style, q, sort }, { push = false, scroll = false, fetch: doFetch = true } = {}) {
    if (q !== undefined) state.q = q;
    if (col !== undefined) { if ((col || '') !== state.col) state.style = ''; state.col = col || ''; }
    if (style !== undefined) state.style = style || '';
    if (cat !== undefined || (state.col && colIndex[state.col])) {
      let next = cat !== undefined && (panels[cat] || cat === 'all') ? cat : state.cat || 'all';
      if (state.col && colIndex[state.col] && (cat === undefined || cat === 'all' || !panels[cat])) next = colIndex[state.col].cat;
      // A new theme gets its own default order (New = newest) unless the URL says otherwise.
      if (next !== state.cat && !sort) state.sort = (panels[next] && panels[next].dataset.feedSort) || 'best-selling';
      state.cat = next;
    }
    if (sort && SORTS.includes(sort)) state.sort = sort;
    if (state.q) { state.col = ''; state.style = ''; }
    if (state.style && !stylesFor(state).some((s) => handleize(s) === handleize(state.style))) state.style = '';
    state.handle = resolveHandle();
    autoLoad = false;
    syncNav();
    setHeading();
    writeUrl(push);
    if (doFetch) load();
    if (scroll) {
      const target = state.col || state.q ? $('.bdg-results') : state.cat === 'all' ? $('.bdg-layout') : panels[state.cat];
      const barH = bar && getComputedStyle(bar).display !== 'none' ? bar.offsetHeight : 0;
      const y = target.getBoundingClientRect().top + scrollY - stickyTop() - barH - 12;
      if (Math.abs(y - scrollY) > 40) scrollTo({ top: y, behavior: REDUCED ? 'auto' : 'smooth' });
    }
  }

  /* ---------------- events ---------------- */
  root.addEventListener('click', (e) => {
    const a = e.target.closest('a[data-bdg-cat], a[data-bdg-col]');
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
    e.preventDefault();
    const col = a.dataset.bdgCol || '';
    const cat = a.dataset.bdgCat || (colIndex[col] || {}).cat || 'all';
    if (sheet.open) sheet.close();
    clearSearch(false);
    if (col) track('bdg_rtp_collection_select', { collection: col, category: cat });
    else track('bdg_rtp_category_select', { category: cat });
    apply({ cat, col, q: '', style: '' }, { push: true, scroll: true });
  });

  stylesEl.addEventListener('click', (e) => {
    const b = e.target.closest('[data-bdg-style]');
    if (!b || e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
    e.preventDefault();
    track('bdg_rtp_style_select', { collection: state.col, style: b.dataset.bdgStyle });
    apply({ style: b.dataset.bdgStyle }, { push: true });
  });

  sortEl && sortEl.addEventListener('change', () => apply({ sort: sortEl.value }));

  moreBtn && moreBtn.addEventListener('click', (e) => {
    e.preventDefault();
    if (state.loading) return;
    autoLoad = true; // after the first click, keep loading as the shopper scrolls
    load({ append: true });
  });
  if (moreBtn && 'IntersectionObserver' in window) {
    new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && autoLoad && !state.loading && state.page < state.pages) load({ append: true });
    }, { rootMargin: '700px' }).observe(moreBtn);
  }
  // server-rendered first page: e.g. 12 rendered -> next fetch is page 1 (duplicates dropped); 36 -> page 2
  state.pages = Math.ceil((+root.dataset.total || 0) / PER) || 1;
  state.page = Math.floor($$('.bdg-card', grid).length / PER);

  const retry = $('[data-bdg-retry]');
  retry && retry.addEventListener('click', () => load());
  window.addEventListener('popstate', (e) => {
    if (e.state && e.state.bdg) { clearSearch(false); if (input) input.value = e.state.q || ''; state.cat = ''; apply({ cat: e.state.cat, col: e.state.col, style: e.state.style, q: e.state.q, sort: e.state.sort }); }
    else fromUrl(false);
  });

  /* ---------------- "Fresh off the press" shelf (lazy) ---------------- */
  const shelf = $('[data-bdg-shelf]');
  if (shelf) {
    const fill = async () => {
      try {
        const payload = await fetchPayload({ handle: master, sort: shelf.dataset.bdgShelf, q: '', style: '' }, 1);
        const items = payload ? Array.from(payload.querySelectorAll('[data-bdg-items] > li')).slice(0, 12) : [];
        if (items.length) $('[data-bdg-shelf-list]', shelf).replaceChildren(...items); else shelf.hidden = true;
      } catch (e) { shelf.hidden = true; }
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((en) => { if (en[0].isIntersecting) { io.disconnect(); fill(); } }, { rootMargin: '400px' });
      io.observe(shelf);
    } else fill();
  }

  /* ---------------- rail tree ---------------- */
  function toggleSub(btn, force) {
    if (!btn) return;
    const open = force !== undefined ? force : btn.getAttribute('aria-expanded') !== 'true';
    if (open) $$('.bdg-tree__toggle[aria-expanded="true"]').forEach((b) => { if (b !== btn) toggleSub(b, false); });
    btn.setAttribute('aria-expanded', String(open));
    const sub = document.getElementById(btn.getAttribute('aria-controls'));
    if (sub) sub.hidden = !open;
  }
  root.addEventListener('click', (e) => { const b = e.target.closest('.bdg-tree__toggle'); if (b) toggleSub(b); });

  /* ---------------- collection filter inside a theme ---------------- */
  const filterCols = (panel) => {
    const f = $('[data-bdg-colfilter]', panel);
    const v = f ? f.value.trim().toLowerCase() : '';
    const lvl = panel.dataset.level || '';
    $$('.bdg-cols > .bdg-col', panel).forEach((li) => { li.hidden = (!!v && !li.dataset.name.includes(v)) || (!!lvl && li.dataset.level !== lvl); });
  };
  root.addEventListener('input', (e) => {
    const f = e.target.closest('[data-bdg-colfilter]');
    if (f) filterCols(f.closest('.bdg-panel'));
  });
  // School level chips (High / Junior high & middle / Elementary) combine with the text finder.
  root.addEventListener('click', (e) => {
    const b = e.target.closest('[data-bdg-level]');
    if (!b) return;
    const panel = b.closest('.bdg-panel');
    panel.dataset.level = b.dataset.bdgLevel;
    $$('[data-bdg-level]', panel).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    track('bdg_rtp_level_select', { level: b.dataset.bdgLevel || 'all' });
    filterCols(panel);
  });

  /* ---------------- Browse sheet: themes left, collections right ---------------- */
  const sheet = $('[data-bdg-sheet]');
  const find = $('[data-bdg-sheet-find]', sheet);
  let sheetOpener = null;
  const tabs = $$('[data-bdg-tab]', sheet);
  function selectTab(slug, focus) {
    const t = tabs.find((x) => x.dataset.bdgTab === slug) || tabs[0];
    if (!t) return;
    tabs.forEach((x) => { const on = x === t; x.setAttribute('aria-selected', String(on)); x.tabIndex = on ? 0 : -1; });
    $$('.bdg-nav2__pane', sheet).forEach((p) => { p.hidden = p.id !== t.getAttribute('aria-controls'); });
    $('.bdg-nav2__panes', sheet).scrollTop = 0;
    if (focus) t.focus();
  }
  sheet.addEventListener('click', (e) => {
    const t = e.target.closest('[data-bdg-tab]');
    if (t) selectTab(t.dataset.bdgTab);
    if (e.target === sheet) sheet.close();
  });
  sheet.addEventListener('keydown', (e) => {
    const t = e.target.closest('[data-bdg-tab]');
    if (!t || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const i = tabs.indexOf(t);
    const n = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : (i + (e.key === 'ArrowDown' ? 1 : -1) + tabs.length) % tabs.length;
    selectTab(tabs[n].dataset.bdgTab, true);
  });
  $$('[data-bdg-sheet-open]').forEach((b) => b.addEventListener('click', () => {
    sheetOpener = b;
    selectTab(panels[state.cat] && !panels[state.cat].dataset.feedSort ? state.cat : '');
    sheet.showModal();
    track('bdg_rtp_browse_open', {});
  }));
  $('[data-bdg-sheet-close]', sheet).addEventListener('click', () => sheet.close());
  sheet.addEventListener('close', () => { if (find && find.value) { find.value = ''; filterSheet(''); } if (sheetOpener) sheetOpener.focus({ preventScroll: true }); });
  function filterSheet(v) {
    sheet.classList.toggle('is-finding', !!v);
    let any = false;
    $$('.bdg-nav2__pane', sheet).forEach((pane) => {
      let n = 0;
      $$('.bdg-col', pane).forEach((li) => { const hit = !v || li.dataset.name.includes(v); li.hidden = !hit; n += hit ? 1 : 0; });
      pane.classList.toggle('is-empty', !n);
      any = any || n > 0;
    });
    let none = $('.bdg-sheet__none', sheet);
    if (v && !any) {
      if (!none) { none = document.createElement('p'); none.className = 'bdg-sheet__none'; $('.bdg-nav2__panes', sheet).prepend(none); }
      none.textContent = `No collection called “${v}”. Close this and search all designs instead.`;
    } else if (none) none.remove();
    if (!v) { const cur = tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0]; if (cur) selectTab(cur.dataset.bdgTab); }
  }
  find && find.addEventListener('input', () => filterSheet(find.value.trim().toLowerCase()));

  /* ---------------- predictive search ---------------- */
  const form = $('[data-bdg-search]');
  const input = form && $('.bdg-search__input', form);
  const box = form && $('.bdg-suggest', form);
  const clearBtn = form && $('[data-bdg-search-clear]', form);
  let sTimer = 0, sCtrl = null, active = -1;
  // Same rule as snippets/bdg-rtp-ok.liquid: premade type, and none of the excluded (trademark / hidden) tags.
  const EXCLUDE_TAGS = (root.dataset.exclude || 'lsu|louisiana state university|rtp-hide').toLowerCase().split('|');
  const isPremade = (p) => (p.type || p.product_type) === 'Premade Transfers' && !(p.tags || []).some((t) => EXCLUDE_TAGS.includes(String(t).toLowerCase()));

  function closeSuggest() { if (!box) return; box.hidden = true; input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); active = -1; }
  function clearSearch(reload = true) {
    if (!input) return;
    input.value = ''; clearBtn.hidden = true; closeSuggest();
    if (reload && state.q) apply({ q: '' }, { push: true });
  }

  async function suggest(q) {
    if (sCtrl) sCtrl.abort();
    sCtrl = new AbortController();
    const url = A.suggestUrl ? A.suggestUrl(q)
      : `${ROOT_URL}search/suggest.json?${new URLSearchParams({ q, 'resources[type]': 'collection,product', 'resources[limit]': 10, 'resources[options][fields]': 'title,product_type,tag,variants.title' })}`;
    try {
      const res = await fetch(url, { signal: sCtrl.signal });
      const r = (await res.json()).resources.results;
      const cols = (r.collections || []).filter((c) => colIndex[c.handle]).slice(0, 4);
      const prods = (r.products || []).filter(isPremade).slice(0, 5);
      let i = 0;
      const opt = (inner, attrs, cls = '') => `<a class="bdg-suggest__opt${cls}" role="option" id="bdg-opt-${i++}" ${attrs}>${inner}</a>`;
      let html = '';
      if (cols.length) {
        html += '<p class="bdg-suggest__group" aria-hidden="true">Collections</p>' + cols.map((c) => {
          const ci = colIndex[c.handle];
          const theme = panels[ci.cat] ? panels[ci.cat].dataset.title : '';
          return opt(`<span class="bdg-suggest__thumb">${ci.img ? `<img class="bdg-zoom-xl" src="${esc(ci.img)}" alt="" width="44" height="44">` : ''}</span><span>${esc(ci.name)}</span><span class="bdg-suggest__meta">${esc(theme)}</span>`,
            `href="${esc(c.url)}" data-bdg-col="${esc(c.handle)}" data-bdg-cat="${esc(ci.cat)}"`);
        }).join('');
      }
      if (prods.length) {
        html += '<p class="bdg-suggest__group" aria-hidden="true">Designs</p>' + prods.map((p) => {
          const u = p.featured_image && p.featured_image.url;
          const src = u ? `${u}${u.includes('?') ? '&' : '?'}width=120` : '';
          return opt(`<span class="bdg-suggest__thumb bdg-suggest__thumb--sq">${src ? `<img class="bdg-full" src="${esc(src)}" alt="" width="44" height="44" loading="lazy">` : ''}</span><span>${esc(p.title.split(/ [–-] /)[0])}</span><span class="bdg-suggest__meta">from $${esc(p.price)}</span>`,
            `href="${esc(p.url)}"`);
        }).join('');
      }
      html += opt(`See all results for “${esc(q)}”`, 'href="#" data-bdg-all="1"', ' bdg-suggest__all');
      box.innerHTML = html;
      box.hidden = false; input.setAttribute('aria-expanded', 'true'); active = -1;
    } catch (e) { if (e.name !== 'AbortError') closeSuggest(); }
  }

  if (form) {
    // rotating examples in the placeholder ("Try “Panthers”") until the shopper types
    const examples = (form.dataset.examples || '').split(',').map((s) => s.trim()).filter(Boolean);
    const basePh = input.placeholder;
    if (examples.length && !REDUCED) {
      let k = 0;
      setInterval(() => {
        if (input.value || document.activeElement === input) return;
        input.classList.add('is-swapping');
        setTimeout(() => { input.placeholder = `Try “${examples[k++ % examples.length]}”`; input.classList.remove('is-swapping'); }, 300);
      }, 3200);
      input.addEventListener('focus', () => { input.placeholder = basePh; });
    }
    input.addEventListener('input', () => {
      const q = input.value.trim();
      clearBtn.hidden = !q;
      clearTimeout(sTimer);
      if (q.length < 2) return closeSuggest();
      sTimer = setTimeout(() => suggest(q), 250);
    });
    input.addEventListener('keydown', (e) => {
      const opts = box ? $$('.bdg-suggest__opt', box) : [];
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (box.hidden || !opts.length) return;
        e.preventDefault();
        active = (active + (e.key === 'ArrowDown' ? 1 : -1) + opts.length) % opts.length;
        opts.forEach((o, n) => o.classList.toggle('is-active', n === active));
        input.setAttribute('aria-activedescendant', opts[active].id);
      } else if (e.key === 'Escape') closeSuggest();
      else if (e.key === 'Enter' && active >= 0 && opts[active]) { e.preventDefault(); opts[active].click(); }
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const q = input.value.trim();
      if (!q) return;
      closeSuggest();
      input.blur();
      track('bdg_rtp_search', { search_term: q });
      apply({ q, col: '', cat: 'all' }, { push: true, scroll: true });
    });
    box.addEventListener('click', (e) => {
      const o = e.target.closest('.bdg-suggest__opt');
      if (!o) return;
      if (o.dataset.bdgAll) { e.preventDefault(); form.requestSubmit(); }
      else if (o.dataset.bdgCol) closeSuggest(); // the root click handler loads the collection in place
      else {
        track('bdg_rtp_search_product', { search_term: input.value.trim(), product: o.getAttribute('href') });
        const m = (o.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
        if (m && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button)) {
          e.preventDefault(); closeSuggest();
          openQuickAdd(decodeURIComponent(m[1]), input, 'search');
        }
      }
    });
    clearBtn.addEventListener('click', () => { clearSearch(); input.focus(); });
    document.addEventListener('click', (e) => { if (!form.contains(e.target)) closeSuggest(); });
  }

  /* ---------------- quick add: grouped size tiles, any number of sizes ---------------- */
  const qa = $('[data-bdg-qa-dialog]');
  const qaBody = $('[data-bdg-qa-body]');
  const qaTitle = $('[data-bdg-qa-title]');
  const qaThumb = $('[data-bdg-qa-thumb]');
  const qaPdp = $('[data-bdg-qa-pdp]');
  const qaDesc = $('[data-bdg-qa-desc]');
  const qaDescBtn = $('[data-bdg-qa-desc-toggle]');
  qaDescBtn && qaDescBtn.addEventListener('click', () => {
    const open = qaDescBtn.getAttribute('aria-expanded') !== 'true';
    qaDescBtn.setAttribute('aria-expanded', String(open)); qaDesc.hidden = !open;
    if (open) track('bdg_rtp_description_open', {});
  });
  let qaOpener = null;
  const productCache = new Map();
  const GROUPS = [['Adult', 10], ['Youth', 6.5], ['Infant & toddler', 3.9], ['Hat, pocket & koozie', 0]];

  // '10.5"-Adult S-L' -> { inches: 10.5, label: '10.5″', fit: 'Adult S–L' }
  const parseSize = (t) => {
    const m = String(t).match(/(\d+(?:\.\d+)?)\s*["”″]/);
    const inches = m ? parseFloat(m[1]) : 999;
    const fit = String(t).replace(/^.*?\d+(?:\.\d+)?\s*["”″]\s*/, '').replace(/^[\s\-–—]+/, '')
      .replace(/\s*-\s*/g, '–').replace(/XLG/g, 'XL').trim();
    // '3.5" – Koozie / Hat Chest' -> fit after the number; 'Koozie/Hat Chest 3.5"' (68 products) -> fit before it
    const before = m ? String(t).slice(0, m.index).replace(/[\s\-–—]+$/, '').trim() : '';
    return { inches, label: m ? `${m[1]}″` : t, fit: fit || before || t };
  };

  async function getProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const url = A.productUrl ? A.productUrl(handle) : `${ROOT_URL}products/${encodeURIComponent(handle)}.js`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(res.status);
    const p = await res.json();
    productCache.set(handle, p);
    return p;
  }

  function renderQa(p) {
    const def = root.dataset.defaultSize || '10.5';
    const vs = p.variants.map((v) => ({ ...v, size: parseSize(v.public_title || v.title) })).sort((a, b) => a.size.inches - b.size.inches);
    const qty = new Map();
    const pre = vs.find((v) => v.available && (v.public_title || v.title).includes(def));
    if (pre) qty.set(pre.id, 1);
    const fi = p.featured_image ? String(p.featured_image) : '';
    qaThumb.innerHTML = '';
    const ttl = p.title.split(/ [–-] /)[0];
    qaTitle.textContent = ttl;
    // The photos a shopper would see on the product page (design on the shirt + the second mockup), shown here so they never need to leave.
    const photos = (p.images && p.images.length ? p.images.map(String) : fi ? [fi] : []).slice(0, 6);
    const at = (u, w) => `${u}${u.includes('?') ? '&' : '?'}width=${w}`;
    const gallery = photos.length ? `<div class="bdg-qa__media">
        <div class="bdg-qa__slides" role="region" aria-label="Photos of ${esc(ttl)}" tabindex="0" data-bdg-slides>
          ${photos.map((u, i) => `<img class="bdg-full" src="${esc(at(u, 900))}" srcset="${[300, 600, 900, 1200].map((w) => `${esc(at(u, w))} ${w}w`).join(', ')}" sizes="(min-width: 750px) 40vw, 130px" width="900" height="900" alt="${esc(ttl)} — photo ${i + 1} of ${photos.length}" ${i ? 'loading="lazy"' : 'fetchpriority="high"'}>`).join('')}
        </div>
        ${photos.length > 1 ? `<div class="bdg-qa__thumbs" role="group" aria-label="Choose photo">${photos.map((u, i) => `<button type="button" class="bdg-qa__thumb-btn" data-photo="${i}" aria-label="Photo ${i + 1} of ${photos.length}" aria-pressed="${i === 0}"><img class="bdg-full" src="${esc(at(u, 160))}" alt="" width="80" height="80"></button>`).join('')}</div>` : ''}
      </div>` : '';
    qaPdp.href = p.url; qaPdp.hidden = false;
    const desc = String(p.description || '').trim();
    qaDesc.innerHTML = desc; qaDesc.hidden = true;
    qaDescBtn.hidden = !desc.replace(/<[^>]+>/g, '').trim(); qaDescBtn.setAttribute('aria-expanded', 'false');
    const tile = (v) => {
      const name = `${v.size.label} ${v.size.fit}`;
      return `<div class="bdg-size${v.available ? '' : ' is-off'}${qty.get(v.id) ? ' is-on' : ''}" data-id="${v.id}" data-price="${v.price}">
        <span class="bdg-size__in">${esc(v.size.label)}</span>
        <span class="bdg-size__fit">${esc(v.size.fit)}</span>
        <span class="bdg-size__price">${v.available ? money(v.price) : 'Sold out'}</span>
        <button type="button" class="bdg-size__pick" ${v.available ? '' : 'disabled'} aria-label="Add ${esc(name)}, ${money(v.price)}"></button>
        <div class="bdg-size__qty">
          <button type="button" data-step="-1" aria-label="One less ${esc(name)}">−</button>
          <output aria-live="polite">${qty.get(v.id) || 0}</output>
          <button type="button" data-step="1" aria-label="One more ${esc(name)}">+</button>
        </div>
      </div>`;
    };
    const groups = GROUPS.map(([label, min], gi) => {
      const max = gi ? GROUPS[gi - 1][1] : Infinity;
      const list = vs.filter((v) => v.size.inches >= min && v.size.inches < max);
      return list.length ? `<p class="bdg-qa__group">${label}</p><div class="bdg-qa__sizes">${list.map(tile).join('')}</div>` : '';
    }).join('');
    qaBody.innerHTML = `
      ${gallery}
      <div class="bdg-qa__panel">
        <p class="bdg-qa__hint">Tap a size to add it. Team order? Add as many sizes as you need.</p>
        ${groups}
        <div class="bdg-qa__foot">
          <div class="bdg-qa__sum"><span data-bdg-qa-n></span><strong data-bdg-qa-c></strong></div>
          <button type="button" class="bdg-btn bdg-qa__add">Add to cart</button>
          <p class="bdg-qa__msg" role="status"></p>
        </div>
      </div>`;
    const slides = $('[data-bdg-slides]', qaBody);
    if (slides) {
      const thumbs = $$('.bdg-qa__thumb-btn', qaBody);
      const mark = (i) => thumbs.forEach((b, k) => b.setAttribute('aria-pressed', String(k === i)));
      slides.addEventListener('scroll', () => mark(Math.round(slides.scrollLeft / (slides.clientWidth || 1))), { passive: true });
      thumbs.forEach((b) => b.addEventListener('click', () => {
        const i = +b.dataset.photo;
        slides.scrollTo({ left: i * slides.clientWidth, behavior: REDUCED ? 'auto' : 'smooth' });
        mark(i);
      }));
    }
    const total = () => {
      let n = 0, c = 0;
      $$('.bdg-size', qaBody).forEach((t) => { const q = qty.get(+t.dataset.id) || 0; n += q; c += q * +t.dataset.price; });
      $('[data-bdg-qa-n]', qaBody).textContent = n ? `${n} transfer${n === 1 ? '' : 's'}` : 'No sizes yet';
      $('[data-bdg-qa-c]', qaBody).textContent = money(c);
      const add = $('.bdg-qa__add', qaBody);
      add.disabled = !n;
      add.textContent = n ? 'Add to cart' : 'Pick a size';
    };
    const setQty = (t, q) => {
      const id = +t.dataset.id;
      q = Math.max(0, Math.min(999, q));
      if (q) qty.set(id, q); else qty.delete(id);
      t.classList.toggle('is-on', q > 0);
      $('output', t).textContent = q;
      total();
    };
    qaBody.onclick = (e) => {
      const t = e.target.closest('.bdg-size');
      if (!t || t.classList.contains('is-off')) return;
      if (e.target.closest('.bdg-size__pick')) { setQty(t, 1); $('[data-step="1"]', t).focus(); return; }
      const b = e.target.closest('[data-step]');
      if (b) {
        const next = (qty.get(+t.dataset.id) || 0) + +b.dataset.step;
        setQty(t, next);
        if (!next) $('.bdg-size__pick', t).focus();
      }
    };
    $('.bdg-qa__add', qaBody).addEventListener('click', () => addToCart(qty, p));
    total();
  }

  /* The ONLY Pursuit-specific cart code. Pursuit's own "add" buttons call fetch('/cart/add.js') and then
     theme.Cart.prototype._onProductAdded(): it re-renders /cart?view=drawer into #sidebar-cart, updates .header_cart_count and opens the drawer.
     We call the same function; if the theme changes and it is gone, we rebuild the drawer ourselves; last resort = /cart. */
  async function cartRefresh() {
    try {
      const T = window.theme;
      if (T && T.Cart && T.Cart.prototype && typeof T.Cart.prototype._onProductAdded === 'function') { T.Cart.prototype._onProductAdded(); return true; }
    } catch (e) { /* fall through */ }
    try {
      const drawer = document.getElementById('sidebar-cart');
      if (!drawer) return false;
      const cartUrl = (window.routes && window.routes.cartUrl) || `${ROOT_URL}cart`;
      const html = await (await fetch(`${cartUrl}?view=drawer&timestamp=${Date.now()}`, { credentials: 'same-origin' })).text();
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('.Cart');
      const cur = drawer.querySelector('.Cart');
      if (!next || !cur) return false;
      cur.replaceWith(next);
      const c = await (await fetch(`${ROOT_URL}cart.js`, { credentials: 'same-origin' })).json();
      document.querySelectorAll('.header_cart_count').forEach((el) => { el.textContent = c.item_count; });
      drawer.classList.add('show');
      document.body.classList.add('show_overlay');
      return true;
    } catch (e) { return false; }
  }

  async function addToCart(qty, p) {
    const items = Array.from(qty, ([id, quantity]) => ({ id, quantity })).filter((i) => i.quantity);
    if (!items.length) return;
    const btn = $('.bdg-qa__add', qaBody);
    const msg = $('.bdg-qa__msg', qaBody);
    btn.disabled = true; btn.setAttribute('aria-busy', 'true'); btn.textContent = 'Adding…'; msg.textContent = '';
    try {
      const json = A.addToCart ? await A.addToCart(items) : await fetch(`${(window.routes && window.routes.cartAddUrl) || ROOT_URL + 'cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      }).then((r) => r.json());
      if (json.status) throw new Error(json.description || json.message || 'Could not add to cart');
      const n = items.reduce((a, i) => a + i.quantity, 0);
      track('bdg_rtp_add_to_cart', { product: p.handle, sizes: items.length, quantity: n });
      qa.close();
      if (!A.addToCart && !(await cartRefresh())) location.href = `${ROOT_URL}cart`;
    } catch (err) {
      msg.textContent = err.message;
      btn.textContent = 'Add to cart';
    } finally {
      btn.disabled = false; btn.removeAttribute('aria-busy');
    }
  }

  async function openQuickAdd(handle, opener, via) {
    qaOpener = opener;
    qaTitle.textContent = 'Loading sizes…'; qaThumb.innerHTML = ''; qaPdp.hidden = true; qaDescBtn.hidden = true; qaDesc.hidden = true;
    qaBody.innerHTML = '<div class="bdg-qa__loading" aria-hidden="true"></div>';
    if (!qa.open) qa.showModal();
    track('bdg_rtp_quick_add', { product: handle, via: via || 'plus' });
    try {
      renderQa(await getProduct(handle));
      // Focus a button, never a text field — an input would pop the phone keyboard.
      const on = $('.bdg-size.is-on [data-step="1"]', qaBody) || $('.bdg-size__pick:not([disabled])', qaBody);
      (on || $('[data-bdg-qa-close]')).focus({ preventScroll: true });
    } catch (err) {
      qaBody.innerHTML = `<p>We couldn't load sizes. <a href="${ROOT_URL}products/${esc(handle)}">Open the product page</a>.</p>`;
    }
  }

  root.addEventListener('click', (e) => {
    const plus = e.target.closest('[data-bdg-qa]');
    if (plus) return openQuickAdd(plus.dataset.bdgQa, plus, 'plus');
    // Clicking the design itself (picture or name) opens the same size sheet: shoppers stay on the page.
    // Ctrl/Cmd/Shift-click or middle-click still opens the product page normally; sold-out designs (no "+") link through.
    const link = e.target.closest('.bdg-card__imglink, .bdg-card__link');
    if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
    const card = link.closest('.bdg-card');
    const btn = card && card.querySelector('[data-bdg-qa]');
    if (!btn) return;
    e.preventDefault();
    openQuickAdd(btn.dataset.bdgQa, btn, 'card');
  });
  $('[data-bdg-qa-close]').addEventListener('click', () => qa.close());
  qa.addEventListener('click', (e) => { if (e.target === qa) qa.close(); });
  qa.addEventListener('close', () => qaOpener && qaOpener.focus({ preventScroll: true }));

  /* ---------------- boot ---------------- */
  function fromUrl(push) {
    const sp = new URLSearchParams(location.search);
    const q = sp.get('q') || '';
    if (input) { input.value = q; clearBtn.hidden = !q; }
    // parameters refine the view this page was rendered for (e.g. /pages/faith-dtf-transfers?col=…)
    const col = sp.get('col') || (sp.has('cat') ? '' : V.viewCol || '');
    const cat = sp.get('cat') || (col ? (colIndex[col] || {}).cat || V.viewCat || 'all' : V.viewCat || (sp.toString() ? 'all' : V.defaultCat || 'all'));
    const styleHandle = sp.get('style') || (V.viewStyle ? handleize(V.viewStyle) : '');
    const style = styleHandle ? stylesFor({ cat, col, q: '' }).find((s) => handleize(s) === styleHandle) || '' : '';
    state.cat = ''; // force the theme's default sort to apply on first load
    apply({ cat, col, style, q, sort: sp.get('sort') || '' }, { push });
  }
  root.classList.add('is-ready');
  const sp0 = new URLSearchParams(location.search);
  if (['cat', 'col', 'q', 'sort', 'style'].some((k) => sp0.has(k)) || (V.defaultCat && V.defaultCat !== 'all' && V.viewKind === 'hub')) fromUrl(false);
  else {
    // the server already rendered this page's view: adopt it without refetching (style pages fetch to get the full filtered first page)
    state.cat = V.viewCat || 'all'; state.col = V.viewCol || ''; state.style = V.viewStyle || '';
    state.handle = resolveHandle();
    syncNav();
    history.replaceState({ bdg: true, cat: state.cat, col: state.col, style: state.style, q: '', sort: state.sort }, '', location.href);
    if (state.style) load();
  }
})();
